import React, { useCallback, useEffect, useRef, useState } from "react";
import "./SemesterList.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { adminRequest } from "../../../utils/requestMethods";
import { BASE_URL } from "../../../utils/config";
import NotFound from "../../../components/NotFound/NotFound";
import OptionsMenu from "../../../components/OptionsMenu/OptionsMenu";
import NoRecord from "../../../components/NoRecord/NoRecord";
import { ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { FaArrowLeftLong, FaPlus } from "react-icons/fa6";
import { IoIosAddCircle, IoIosMore } from "react-icons/io";

const SemesterList = () => {
  const headers = ["Semester", "Is Running", "Status"];
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleMenu, setVisibleMenu] = useState(null);
  const menuRefs = useRef([]);
  const location = useLocation();
  const navigate = useNavigate();
  const activeURL = location.pathname.split("/")[4];

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await adminRequest.post(
        `${BASE_URL}/course/getSemesters`,
        {
          uuid: activeURL,
        },
      );
      const fetchedRows = response.data.data.map((s) => ({
        displayData: [
          s.semester,
          s.is_running_semester ? "YES" : "NO",
          s.status,
        ],
        uuid: s.uuid,
        status: s.status,
        is_running_semester: s.is_running_semester,
      }));
      setRows(fetchedRows);
    } catch (error) {
      <NotFound />;
    } finally {
      setIsLoading(false);
    }
  }, [activeURL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBlock = async (uuid) => {
    Swal.fire({
      title: "Are you sure you want to block this semester?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#264796",
      cancelButtonColor: "#FC0000",
      confirmButtonText: "Block",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/course/semester/block`,
            {
              uuid: uuid,
            },
          );

          Swal.fire({
            title: "Blocked!",
            text: `${response.data.message}`,
            icon: "success",
          });

          fetchData();
        } catch (error) {
          Swal.fire({
            title: "Failed!",
            text: `${error.response.data.message}`,
            icon: "error",
          });
        }
      }
    });
  };

  const handleUnblock = async (uuid) => {
    Swal.fire({
      title: "Are you sure you want to unblock this semester?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#264796",
      cancelButtonColor: "#FC0000",
      confirmButtonText: "Unblock",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/course/semester/unblock`,
            {
              uuid: uuid,
            },
          );
          Swal.fire({
            title: "Unblocked!",
            text: `${response.data.message}`,
            icon: "success",
          });
          fetchData();
        } catch (error) {
          Swal.fire({
            title: "Failed!",
            text: `${error.response.data.message}`,
            icon: "error",
          });
        }
      }
    });
  };

  const markRunning = async (uuid) => {
    Swal.fire({
      title: "Are you sure you want to mark this semester as running?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#264796",
      cancelButtonColor: "#FC0000",
      confirmButtonText: "Confirm",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/course/semester/markRunning`,
            {
              uuid: uuid,
            },
          );

          Swal.fire({
            title: "MARKED RUNNING!",
            text: `${response.data.message}`,
            icon: "success",
          });

          fetchData();
        } catch (error) {
          Swal.fire({
            title: "Failed!",
            text: `${error.response.data.message}`,
            icon: "error",
          });
        }
      }
    });
  };

  const markInActive = async (uuid) => {
    Swal.fire({
      title: "Are you sure you want to mark this semester as inactive?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#264796",
      cancelButtonColor: "#FC0000",
      confirmButtonText: "Confirm",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/course/semester/markInactive`,
            {
              uuid: uuid,
            },
          );

          Swal.fire({
            title: "MARKED INACTIVE!",
            text: `${response.data.message}`,
            icon: "success",
          });

          fetchData();
        } catch (error) {
          Swal.fire({
            title: "Failed!",
            text: `${error.response.data.message}`,
            icon: "error",
          });
        }
      }
    });
  };

  const getMenuItems = (row) => [
    row.is_running_semester
      ? {
          link: "#",
          text: "Mark Inactive",
          onClick: (e) => {
            e.preventDefault();
            markInActive(row.uuid);
          },
        }
      : {
          link: "#",
          text: "Mark Running",
          onClick: (e) => {
            e.preventDefault();
            markRunning(row.uuid);
          },
        },
    row.status !== "BLOCKED"
      ? {
          link: "#",
          text: "Block",
          onClick: (e) => {
            e.preventDefault();
            handleBlock(row.uuid);
          },
        }
      : {
          link: "#",
          text: "Unblock",
          onClick: (e) => {
            e.preventDefault();
            handleUnblock(row.uuid);
          },
        },
  ];

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleCreateButtonClick = () => {
    navigate("create");
  };

  const handleAssignClick = () => {
    navigate("assign");
  };
  const handleMoreClick = (rowIndex) => {
    setVisibleMenu(visibleMenu === rowIndex ? null : rowIndex);
  };

  return (
    <div className="semesterListContainer">
      <div className="semesterListContents">
        <div className="top">
          <span className="leftTitle">
            <FaArrowLeftLong onClick={handleBackClick} className="icon" />
            Semester List
          </span>
          <div className="rightContents">
            <span className="assignButton" onClick={handleAssignClick}>
              <span className="title">Assign Section</span>
              <span className="csvIcon">
                <FaPlus />
              </span>
            </span>
            <span className="createButton" onClick={handleCreateButtonClick}>
              <span className="title">Create Semester</span>
              <span className="addIcon">
                <IoIosAddCircle />
              </span>
            </span>
          </div>
        </div>
        <div className="bottom">
          <table className="listTable">
            <thead>
              <tr className="tableHeaderRow">
                <th></th>
                {headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows && rows.length > 0 ? (
                rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td
                      style={{ position: "relative" }}
                      ref={(el) => (menuRefs.current[rowIndex] = el)}
                    >
                      <IoIosMore onClick={() => handleMoreClick(rowIndex)} />
                      {visibleMenu === rowIndex && (
                        <OptionsMenu
                          menuItems={getMenuItems(row)}
                          visible={true}
                        />
                      )}
                    </td>
                    {Array.isArray(row.displayData) &&
                      row.displayData.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={headers.length + 1}
                    style={{ fontSize: "14px", borderBottom: "none" }}
                  >
                    <NoRecord />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ToastContainer position="top-center" />
    </div>
  );
};

export default SemesterList;
